import { generateHash } from 'crm-index-visualization-core/core/model/hash';
import { datesHash } from '../util/storageMangager';
import { denormalizeVizType, normalizeVizType, pageTypeToVizType } from './vizSetting';
/**
 * Immutable metadata representation of a backend View object.
 *
 * Provides lightweight view identity, ownership, and permissions data extracted from backend Views.
 * Designed for client-side draft management with hash-based storage and optimistic updates.
 *
 * **Data Flow**: Backend View → ViewMeta → Client Storage → Draft State → useViewMeta/useMutateViewMeta hooks
 *
 * **Immutability**: Use `with()` method for updates. All properties are readonly.
 *
 * @example
 * ```typescript
 * // Reading metadata
 * const { data: viewMeta } = useViewMeta();
 * const isOwned = viewMeta?.isOwnedBy(currentUser.id);
 *
 * // Updating metadata
 * const [saveViewMeta] = useMutateViewMeta();
 * saveViewMeta(viewMeta.with({ name: 'New Name' }));
 * ```
 */
class ViewMeta {
  /** Display name of the view */

  /** Unique identifier for the view, fallback to idLabel if it exists (Index page convention) **/

  /** User ID of the view creator/owner. Use isOwnedBy(userId) or isHubSpotDefined for ownership checks. Default views have ownerId = -1 */

  /** Indicates which visualization type is active for this view (e.g table, BOARD, REPORT, etc) */

  /** Object type ID of the view */

  /** Color of the view tab in the UI */

  /**
   * Creates a new ViewMeta instance from a backend view object or POJO.
   *
   * Extracts and normalizes essential metadata from the full backend view,
   * creating a lightweight object focused only on view identity and permissions.
   *
   * @param param0 Backend view object or POJO containing view metadata
   * @throws {Error} If required properties are missing or invalid
   */
  constructor({
    name,
    id,
    ownerId,
    currentViewVisualization = 'TABLE',
    viewColor,
    idLabel,
    objectTypeId
  }) {
    this.currentViewVisualization = 'TABLE';
    this.name = name;
    this.id = idLabel !== null && idLabel !== void 0 ? idLabel : id.toString();
    this.idLabel = idLabel;
    // Apply same logic as parseViewFromBEModel: default views (with idLabel) get ownerId = -1
    this.ownerId = idLabel ? -1 : ownerId;
    if (currentViewVisualization) {
      this.currentViewVisualization = pageTypeToVizType(currentViewVisualization);
    }
    this.viewColor = viewColor;
    this.objectTypeId = objectTypeId;
  }

  /**
   * Creates a new ViewMeta with updated properties.
   * This is the recommended way to modify ViewMeta to ensure immutability.
   *
   * Note: The view ID cannot be changed after creation as it's an immutable identifier.
   *
   * @example
   * // Update view name
   * const updatedViewMeta = viewMeta.with({
   *   name: 'New View Name'
   * });
   *
   * @example
   * // Update multiple properties
   * const updatedViewMeta = viewMeta.with({
   *   name: 'New Name',
   *   ownerId: 123
   * });
   *
   * @param updates Partial updates to apply to the view metadata (excluding id)
   * @returns A new ViewMeta instance with the updates applied
   */
  with(updates) {
    var _updates$name, _updates$ownerId, _updates$currentViewV, _updates$viewColor;
    return new ViewMeta({
      name: (_updates$name = updates.name) !== null && _updates$name !== void 0 ? _updates$name : this.name,
      id: this.id,
      // ID is immutable - always use the current ID
      ownerId: (_updates$ownerId = updates.ownerId) !== null && _updates$ownerId !== void 0 ? _updates$ownerId : this.ownerId,
      currentViewVisualization: updates.pageType ? normalizeVizType(updates.pageType) : (_updates$currentViewV = updates.currentViewVisualization) !== null && _updates$currentViewV !== void 0 ? _updates$currentViewV : this.currentViewVisualization,
      viewColor: (_updates$viewColor = updates.viewColor) !== null && _updates$viewColor !== void 0 ? _updates$viewColor : this.viewColor,
      objectTypeId: this.objectTypeId
    });
  }

  /**
   * Generates a unique hash for this ViewMeta based on its content.
   *
   * This hash is used as the unique identifier when storing the ViewMeta
   * in client storage for draft purposes.
   *
   * @returns Promise resolving to the unique hash string
   */
  hash() {
    return generateHash(this);
  }

  /**
   * Loads a ViewMeta from the client cache using its hash.
   *
   * This method is used by the draft system to retrieve previously saved
   * ViewMeta objects from client storage.
   *
   * @param hash The unique hash identifier for the ViewMeta
   * @param clientStorage Storage implementation for accessing client cache
   * @throws {Error} If ViewMeta is not found in cache with descriptive message
   * @throws {Error} If stored data cannot be extracted from cache
   * @throws {Error} If storage access fails
   * @returns Promise resolving to the loaded ViewMeta instance
   */
  static async fromClientCache(hash, clientStorage) {
    const viewMetaJson = await clientStorage.getItem(ViewMeta.getClientCacheKey(hash));
    if (!viewMetaJson) {
      throw new Error(`No ViewMeta draft found in client cache for hash: ${hash}`);
    }
    try {
      const parsedData = JSON.parse(viewMetaJson);
      return new ViewMeta(parsedData);
    } catch (parseError) {
      const errorMessage = parseError instanceof Error ? parseError.message : 'Unknown error';
      throw new Error(`Failed to parse ViewMeta from client cache (hash: ${hash}). ` + `Data may be corrupted. Original error: ${errorMessage}`);
    }
  }
  get pageType() {
    return denormalizeVizType(this.currentViewVisualization);
  }

  /**
   * Saves this ViewMeta to the client cache and returns its unique hash.
   *
   * This method is a core part of the draft system that enables client-side
   * editing without immediate backend persistence. It:
   *
   * 1. Generates a unique hash for this ViewMeta based on its content
   * 2. Stores the serialized ViewMeta in client storage under a key derived from the hash
   * 3. Returns the hash, which can be used later to retrieve the ViewMeta
   *
   * ## Usage with Hooks
   *
   * Generally, you should not need to call this method directly. Instead:
   *
   * ```typescript
   * const [saveViewMeta] = useMutateViewMeta();
   *
   * // This internally calls toClientCache and maintains the edit hash state
   * saveViewMeta(updatedViewMeta);
   * ```
   *
   * ## Storage Approach
   *
   * The ViewMeta is stored as a JSON string. The key is derived from the hash
   * using the format `viewmeta-{hash}`. This system allows multiple drafts to
   * exist simultaneously without conflicting.
   *
   * @param clientStorage Storage implementation for storing in client cache
   * @throws {Error} If hash generation fails
   * @throws {Error} If storage access fails or quota is exceeded
   * @returns Promise resolving to the unique hash of the saved ViewMeta
   */
  async toClientCache(clientStorage) {
    const hash = await this.hash();
    const key = ViewMeta.getClientCacheKey(hash);
    await Promise.all([clientStorage.setItem(key, JSON.stringify(this)), datesHash(key, Date.now(), clientStorage)]);
    return hash;
  }

  /**
   * Generates the storage key used for storing/retrieving ViewMeta in client storage.
   *
   * This utility method ensures consistent key formatting between save and load operations.
   *
   * @param hash The ViewMeta hash to generate a key for
   * @returns The formatted storage key (format: 'viewmeta-{hash}')
   */
  static getClientCacheKey(hash) {
    return `viewmeta-${hash}`;
  }

  /**
   * Gets the client cache key for this ViewMeta instance.
   *
   * @returns Promise resolving to the storage key for this ViewMeta
   */
  async getClientCacheKey() {
    return ViewMeta.getClientCacheKey(await this.hash());
  }

  /**
   * Determines if the view is owned by the specified user.
   *
   * This method provides a convenient way to check ownership for
   * authorization decisions in the UI.
   *
   * @param userId The ID of the user to check ownership against
   * @returns True if the specified user is the owner of this view
   */
  isOwnedBy(userId) {
    return this.ownerId === userId;
  }

  /**
   * Determines if the view is HubSpot-defined.
   *
   * HubSpot-defined views have an owner ID of -1.
   *
   * @returns True if the view is HubSpot-defined, false otherwise
   */
  get isHubSpotDefined() {
    return this.ownerId === -1;
  }

  /**
   * Determines if a user can edit this view based on ownership and scopes.
   *
   * This method implements the same authorization logic as the useCanUserEditView hook:
   * - The user can edit if they are the owner of the view
   * - For non-HubSpot-defined views, the user can also edit if they have the 'crm-view-write' scope
   *
   * @param userId The ID of the user attempting to edit
   * @param userScopes Set of permission scopes the user has
   * @returns True if the user can edit this view, false otherwise
   */
  isEditableBy(userId, userScopes) {
    return this.isOwnedBy(userId) || !this.isHubSpotDefined && userScopes.has('crm-view-write');
  }

  /**
   * Returns the view ID or label, depending on which is available.
   *
   * There's a quirk for view where we use negative id for default views (i.e all view)
   * and we index page instead use label as an alternative for id.
   *
   * This getter implements the logic to return the correct value.
   *
   * @deprecated('Use id instead'), following index page convention
   * @returns The view ID or label
   */
  get idOrLabel() {
    return this.id;
  }

  /**
   * Determines if a user can make this view public based on BET scopes.
   *
   * @param userScopes Set of permission scopes the user has
   * @returns True if the user can make this view public, false otherwise
   */
  static canMakePublic(userScopes) {
    return !userScopes.has('bet-views-public-share-restricted') || userScopes.has('bet-views-public-share');
  }

  /**
   * Converts this ViewMeta to a backend view by applying its properties.
    * @param backendView The base backend view to apply changes to
   * @returns A new backend view with ViewMeta properties applied
   */
  toBEView(backendView) {
    return Object.assign({}, backendView, this, {
      idLabel: this.idLabel,
      objectTypeId: this.objectTypeId
    });
  }

  /**
   * Get the view ID for the index page.
   */
  static getIndexPageViewId(view) {
    var _view$idLabel;
    return (_view$idLabel = view.idLabel) !== null && _view$idLabel !== void 0 ? _view$idLabel : view.id.toString();
  }
}
export { ViewMeta };